import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import { openApi } from '../../../helpers/request'
import i from '../../../translations/i'

export type StatusRole = 'none' | 'initial' | 'completed' | 'canceled'

// Роли взаимоисключающие: бэкенд не примет статус с двумя флагами сразу,
// а при сохранении сам снимает роль с того статуса, у которого она была.
export const ROLE_FLAGS: Record<Exclude<StatusRole, 'none'>, string> = {
    initial: 'is_initial',
    completed: 'is_completed',
    canceled: 'is_canceled',
}

export const ROLE_COLORS: Record<StatusRole, string> = {
    none: 'default',
    initial: 'blue',
    completed: 'green',
    canceled: 'red',
}

export const statusRole = (status: any): StatusRole => {
    if (status?.is_initial) return 'initial'
    if (status?.is_completed) return 'completed'
    if (status?.is_canceled) return 'canceled'

    return 'none'
}

export const DEFAULT_COLOR = '7264D6'

/** Можно ли отменить заказ в приложении, пока он в этом статусе */
export const allowsCancel = (status: any): boolean => Number(status?.allow_cancel) > 0

/** Снимать ли холд и возвращать деньги, когда пользователь отменяет заказ */
export const refundsOnCancel = (status: any): boolean => allowsCancel(status) && Number(status?.refund_on_cancel) > 0

/** Списывать ли захолдированные деньги, когда заказ переходит в этот статус */
export const capturesPayment = (status: any): boolean => Number(status?.capture_payment) > 0

/** С этого статуса заказ можно оценить в приложении */
export const allowsOrderReview = (status: any): boolean => Number(status?.review_available) > 0

/** С этого статуса товары заказа считаются купленными — для отзыва «только после покупки» */
export const confirmsPurchase = (status: any): boolean => Number(status?.purchase_confirmed) > 0

/** В отменённом статусе холд снимают, а не списывают — бэкенд тоже сбросит флаг */
export const captureUnavailable = (role: StatusRole): boolean => role === 'canceled'

/** У выполненного и уже отменённого заказа отменять нечего — бэкенд тоже сбросит флаг */
export const cancelUnavailable = (role: StatusRole): boolean => role === 'completed' || role === 'canceled'

const STRING_FIELDS = ['name', 'color_hex', 'icon', 'id_crm', 'name_button_enter', 'name_button_complete']

const emptyItem = () => ({
    id: 0,
    name: '',
    color_hex: DEFAULT_COLOR,
    icon: '',
    id_crm: '',
    name_button_enter: '',
    name_button_complete: '',
    is_initial: false,
    is_completed: false,
    is_canceled: false,
    allow_cancel: false,
    refund_on_cancel: false,
    capture_payment: false,
    review_available: false,
    purchase_confirmed: false,
})

class OrderStatusController {

    list: any[] = []
    listLoaded: boolean = false

    item: any = emptyItem()
    itemLoaded: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    getList() {
        return openApi('get', `/order_status?limit=1000&page=1`)
            .then(result => runInAction(() => {
                this.list = result.data
                this.listLoaded = true
            }))
            .catch(error => console.log(error?.response?.data))
    }

    getOne(id: number) {
        // Сразу после создания id уже проставлен — форму не прячем, только освежаем
        if (this.item.id !== id) this.itemLoaded = false

        if (!id) {
            this.item = emptyItem()
            this.itemLoaded = true
            return
        }

        openApi('get', `/order_status/${id}`)
            .then(result => runInAction(() => {
                const item: any = { ...emptyItem(), ...result.data }
                // Пустые строки бэкенд хранит как null — инпутам нужна строка
                STRING_FIELDS.forEach(key => item[key] = item[key] ?? '')
                if (item.color_hex === '') item.color_hex = DEFAULT_COLOR
                item.refund_on_cancel = refundsOnCancel(item)
                item.allow_cancel = allowsCancel(item)
                item.capture_payment = capturesPayment(item)
                item.review_available = allowsOrderReview(item)
                item.purchase_confirmed = confirmsPurchase(item)

                this.item = item
                this.itemLoaded = true
            }))
            .catch(error => console.log(error?.response?.data))
    }

    set(key: string, value: any) {
        this.item[key] = value
    }

    setRole(role: StatusRole) {
        Object.entries(ROLE_FLAGS).forEach(([flagRole, flag]) => {
            this.item[flag] = flagRole === role
        })

        if (cancelUnavailable(role)) this.setAllowCancel(false)
        if (captureUnavailable(role)) this.item.capture_payment = false
    }

    setAllowCancel(value: boolean) {
        this.item.allow_cancel = value
        // Возврат — часть отмены пользователем, без неё он не срабатывает
        if (!value) this.item.refund_on_cancel = false
    }

    /** Статус, у которого сейчас есть роль — чтобы предупредить, что она перейдёт к этому */
    roleOwner(role: StatusRole) {
        if (role === 'none') return null

        return this.list.find((el: any) => el.id !== this.item.id && statusRole(el) === role) ?? null
    }

    save(): Promise<any> {
        const data = new FormData()
        STRING_FIELDS.forEach(key => data.append(key, this.item[key] ?? ''))
        Object.values(ROLE_FLAGS).forEach(flag => data.append(flag, `${+this.item[flag]}`))
        data.append('allow_cancel', `${+this.item.allow_cancel}`)
        data.append('refund_on_cancel', `${+this.item.refund_on_cancel}`)
        data.append('capture_payment', `${+this.item.capture_payment}`)
        data.append('review_available', `${+this.item.review_available}`)
        data.append('purchase_confirmed', `${+this.item.purchase_confirmed}`)

        const url = this.item.id > 0 ? `/order_status/${this.item.id}` : `/order_status`

        return openApi('post', url, data)
            .then(result => {
                runInAction(() => this.item.id = result.data.id)
                notification.success({
                    message: i.t('def:success.success'),
                    description: i.t('def:success.edit'),
                })
                this.getList()

                return result.data
            })
            .catch(error => {
                notification.error({
                    message: i.t('def:errors.error'),
                    description: error?.response?.data?.data?.message ?? '',
                })

                throw error
            })
    }

    remove(id: number): Promise<boolean> {
        if (!window.confirm(i.t('def:confirms.delete'))) return Promise.resolve(false)

        return openApi('delete', `/order_status/${id}`)
            .then(() => {
                this.getList()

                return true
            })
            .catch(error => {
                const message = error?.response?.data?.data?.message
                notification.error({
                    message: i.t('def:errors.error'),
                    description: message === 'required' ? i.t('orderStatus:systemNoDelete') : (message ?? ''),
                })

                return false
            })
    }
}

export default new OrderStatusController()
